// web/src/shell/ErrorBoundary.tsx
//
// Wraps Sentry's ErrorBoundary so render errors below us are auto-reported
// (when Sentry is initialised) and the user sees a styled fallback with a
// "Try again" button rather than a blank screen.
//
// Mount at two levels:
//   1. Top of <App /> — catches anything during initial mount / cross-route.
//   2. Inside the trading route — a crash in the pane shouldn't kill the
//      sidebar so the user can still navigate to /account or /pricing.
import { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";
import type { ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getConsent } from "./CookieConsent";

interface Props {
  /** Children to protect from render errors. */
  children: ReactNode;
  /** Short label shown in the fallback, e.g. "Trading dashboard". */
  scope?: string;
}

interface FallbackProps {
  error: unknown;
  resetError: () => void;
  scope?: string;
}

function Fallback({ error, resetError, scope }: FallbackProps) {
  const message =
    error instanceof Error ? error.message : "An unexpected error occurred.";
  // Only claim "the error has been reported" when telemetry was actually
  // enabled. Sentry doesn't init until the user has accepted cookies, so
  // declining users would otherwise read a false reassurance.
  const reportedToSentry = getConsent() === "accepted";
  return (
    <div className="flex min-h-[400px] items-center justify-center p-6">
      <div className="max-w-md space-y-4 rounded-lg border border-border bg-card p-8 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle className="h-6 w-6" aria-hidden="true" />
        </div>
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">
            {scope ? `${scope} hit an error` : "Something went wrong"}
          </h2>
          <p className="text-sm text-muted-foreground">{message}</p>
          <p className="text-xs text-muted-foreground">
            {reportedToSentry
              ? "The error has been reported. You can retry or reload the page."
              : "You can retry or reload the page."}
          </p>
        </div>
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" onClick={resetError}>
            <RotateCcw className="me-2 h-4 w-4" /> Try again
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Reload page
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ErrorBoundary({ children, scope }: Props) {
  return (
    <SentryErrorBoundary
      fallback={({ error, resetError }) => (
        <Fallback error={error} resetError={resetError} scope={scope} />
      )}
    >
      {children}
    </SentryErrorBoundary>
  );
}
